import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Pix as PixIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, isAfter, startOfDay } from 'date-fns';
import api from '../../services/api';

interface DestinatarioData {
  endToEndId: string;
  chave: string;
  tipo: string;
  proprietario: {
    identificador: string;
    nome: string;
    tipo: string;
  };
}

interface PagamentoResultado {
  endToEndId: string;
  estado: string;
  valor: number;
  descricao: string;
  horario: string;
  dataAgendamento?: string;
}

const steps = ['Informar Chave', 'Confirmar Dados', 'Resultado'];

export default function PixEnviarPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Step 1: Chave PIX
  const [chave, setChave] = useState('');
  const [destinatario, setDestinatario] = useState<DestinatarioData | null>(null);

  // Step 2: Valor e confirmação
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [dataAgendamento, setDataAgendamento] = useState<Date | null>(null);

  // Step 3: Resultado
  const [resultado, setResultado] = useState<PagamentoResultado | null>(null);

  const handleBuscarChave = async () => {
    if (!chave.trim()) {
      setError('Informe a chave PIX');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/pix/enviar/iniciar', {
        chave: chave.trim(),
      });

      if (response.success) {
        setDestinatario(response.data);
        setActiveStep(1);
      } else {
        setError(response.error?.message || 'Erro ao buscar chave PIX');
      }
    } catch (err: any) {
      setError(err.error?.message || err.message || 'Erro ao buscar chave PIX');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPagamento = async () => {
    if (!valor || parseFloat(valor) <= 0) {
      setError('Informe um valor válido');
      return;
    }

    if (!destinatario) {
      setError('Destinatário não encontrado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        endToEndId: destinatario.endToEndId,
        valor: parseFloat(valor),
        descricao: descricao || undefined,
        chave: chave.trim(), // Original PIX key for alternative endpoint
        destino: {
          chave: chave.trim(), // PIX key for fallback
          cpfCnpj: destinatario.proprietario.identificador,
          nome: destinatario.proprietario.nome,
          conta: '',
          agencia: '',
          tipo: 'CORRENTE',
        },
      };

      if (dataAgendamento && isAfter(startOfDay(dataAgendamento), startOfDay(new Date()))) {
        payload.dataAgendamento = format(dataAgendamento, 'yyyy-MM-dd');
      }

      const response = await api.post('/pix/enviar/confirmar', payload);

      if (response.success) {
        setResultado(response.data);
        setSuccess(response.message);
        setActiveStep(2);
      } else {
        setError(response.error?.message || 'Erro ao confirmar pagamento');
      }
    } catch (err: any) {
      setError(err.error?.message || err.message || 'Erro ao confirmar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setDestinatario(null);
    }
  };

  const handleNovoPagamento = () => {
    setActiveStep(0);
    setChave('');
    setDestinatario(null);
    setValor('');
    setDescricao('');
    setDataAgendamento(null);
    setResultado(null);
    setError(null);
    setSuccess(null);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copiado para a área de transferência');
    setTimeout(() => setSuccess(null), 2000);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCpfCnpj = (value: string) => {
    if (!value) return '-';
    const clean = value.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const getKeyTypeLabel = (tipo: string) => {
    const tipos: Record<string, string> = {
      'CPF': 'CPF',
      'CNPJ': 'CNPJ',
      'EMAIL': 'E-mail',
      'TELEFONE': 'Telefone',
      'EVP': 'Chave Aleatória',
    };
    return tipos[tipo?.toUpperCase()] || tipo;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Enviar PIX
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Transfira valores instantaneamente usando uma chave PIX
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Step 1: Informar Chave */}
        {activeStep === 0 && (
          <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PixIcon color="primary" sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Informe a Chave PIX
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CPF, CNPJ, E-mail, Telefone ou Chave Aleatória
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Chave PIX"
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              placeholder="Ex: 12345678900, email@exemplo.com, +5511999999999"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleBuscarChave} disabled={loading}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              onKeyPress={(e) => e.key === 'Enter' && handleBuscarChave()}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleBuscarChave}
              disabled={loading || !chave.trim()}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            >
              Buscar Destinatário
            </Button>
          </Paper>
        )}

        {/* Step 2: Confirmar Dados */}
        {activeStep === 1 && destinatario && (
          <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleVoltar}
              sx={{ mb: 3 }}
            >
              Voltar
            </Button>

            {/* Destinatário */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  DESTINATÁRIO
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'primary.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {destinatario.proprietario.tipo === 'FISICO' ? (
                      <PersonIcon color="primary" />
                    ) : (
                      <BusinessIcon color="primary" />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {destinatario.proprietario.nome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCpfCnpj(destinatario.proprietario.identificador)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={getKeyTypeLabel(destinatario.tipo)}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            {/* Valor */}
            <TextField
              fullWidth
              label="Valor"
              type="number"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              inputProps={{ min: 0.01, step: 0.01 }}
              sx={{ mb: 3 }}
            />

            {/* Descrição */}
            <TextField
              fullWidth
              label="Descrição (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Pagamento de serviço"
              inputProps={{ maxLength: 140 }}
              helperText={`${descricao.length}/140 caracteres`}
              sx={{ mb: 3 }}
            />

            {/* Agendamento */}
            <DatePicker
              label="Data de Agendamento (opcional)"
              value={dataAgendamento}
              onChange={(date) => setDataAgendamento(date)}
              minDate={new Date()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Deixe em branco para transferência imediata',
                },
              }}
              sx={{ mb: 4 }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              color="success"
              onClick={handleConfirmarPagamento}
              disabled={loading || !valor || parseFloat(valor) <= 0}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            >
              {dataAgendamento ? 'Agendar PIX' : 'Enviar PIX'}
            </Button>
          </Paper>
        )}

        {/* Step 3: Resultado */}
        {activeStep === 2 && resultado && (
          <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            </Box>

            <Typography variant="h5" fontWeight={600} gutterBottom>
              {resultado.estado === 'AGENDADO' ? 'PIX Agendado!' : 'PIX Enviado!'}
            </Typography>

            <Typography variant="h3" fontWeight={700} color="primary" sx={{ my: 3 }}>
              {formatCurrency(resultado.valor)}
            </Typography>

            <Card variant="outlined" sx={{ textAlign: 'left', mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      ID da Transação
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {resultado.endToEndId}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopy(resultado.endToEndId)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      <Chip
                        size="small"
                        label={resultado.estado}
                        color={resultado.estado === 'REALIZADO' ? 'success' : 'info'}
                      />
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Data/Hora
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {resultado.horario
                        ? new Date(resultado.horario).toLocaleString('pt-BR')
                        : '-'}
                    </Typography>
                  </Grid>

                  {resultado.dataAgendamento && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Agendado para
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {format(new Date(resultado.dataAgendamento), 'dd/MM/yyyy', { locale: ptBR })}
                      </Typography>
                    </Grid>
                  )}

                  {resultado.descricao && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Descrição
                      </Typography>
                      <Typography variant="body2">
                        {resultado.descricao}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleNovoPagamento}
              startIcon={<PixIcon />}
            >
              Novo PIX
            </Button>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
}
